import { toast } from '@heroui/react';
import { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import {
  Button,
  Description,
  FieldError,
  Input,
  Label,
  ListBox,
  Modal,
  NumberField,
  Select,
  TextField,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import {
  FeatureConfigEditor,
  createEmptyGroup,
  flattenFeatureGroups,
  parseFeatureConfigToGroups,
  type FeatureConfigItem,
} from './FeatureConfigEditor';

const SALE_TYPES = ['SUBSCRIPTION', 'ONE_TIME'] as const;
const BILLING_PERIOD_UNITS = ['MONTH', 'YEAR', 'NONE'] as const;
const SKU_STATUSES = ['ACTIVE', 'INACTIVE'] as const;
const PLAN_TIERS = [0, 10, 20, 30] as const;

const SALE_TYPE_LABELS: Record<(typeof SALE_TYPES)[number], string> = {
  SUBSCRIPTION: '订阅型',
  ONE_TIME: '一次性',
};

const BILLING_PERIOD_LABELS: Record<(typeof BILLING_PERIOD_UNITS)[number], string> = {
  MONTH: '月',
  YEAR: '年',
  NONE: '无',
};

const STATUS_LABELS: Record<(typeof SKU_STATUSES)[number], string> = {
  ACTIVE: '在售',
  INACTIVE: '停售',
};

const TIER_LABELS: Record<(typeof PLAN_TIERS)[number], string> = {
  0: 'FREE (0)',
  10: 'STD (10)',
  20: 'PRO (20)',
  30: 'ULT (30)',
};

const featureChildSchema = z.object({
  code: z.string(),
  title: z.string(),
  description: z.string(),
  sort: z.coerce.number().int().min(1),
});

const featureGroupSchema = z.object({
  code: z.string(),
  title: z.string(),
  sort: z.coerce.number().int().min(1),
  children: z.array(featureChildSchema),
});

const skuSchema = z
  .object({
    code: z
      .string()
      .min(1, 'SKU 编码不能为空')
      .max(64, 'SKU 编码长度不能超过 64 字符')
      .regex(/^[A-Z0-9_]+$/, 'SKU 编码只能使用大写字母、数字与下划线'),
    name: z.string().min(1, 'SKU 名称不能为空').max(128, 'SKU 名称长度不能超过 128 字符'),
    saleType: z.enum(SALE_TYPES),
    billingPeriodUnit: z.enum(BILLING_PERIOD_UNITS),
    billingPeriodCount: z.coerce.number().int().min(0),
    defaultSeatQuantity: z.coerce.number().int().min(1).nullable().optional(),
    minQuantity: z.coerce.number().int().min(1).nullable().optional(),
    maxQuantity: z.coerce.number().int().min(1).nullable().optional(),
    tier: z.coerce.number().int().min(0).nullable().optional(),
    status: z.enum(SKU_STATUSES),
    featureGroups: z.array(featureGroupSchema).optional(),
  })
  .superRefine((values, ctx) => {
    if (values.saleType === 'SUBSCRIPTION' && values.billingPeriodUnit === 'NONE') {
      ctx.addIssue({
        code: 'custom',
        path: ['billingPeriodUnit'],
        message: '订阅型 SKU 的计费周期不能为「无」',
      });
    }
    if (values.saleType === 'ONE_TIME') {
      if (values.billingPeriodUnit !== 'NONE') {
        ctx.addIssue({
          code: 'custom',
          path: ['billingPeriodUnit'],
          message: '一次性 SKU 的计费周期必须为「无」',
        });
      }
      if (values.billingPeriodCount !== 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['billingPeriodCount'],
          message: '一次性 SKU 的基础周期数量必须为 0',
        });
      }
    }
  });

type SkuFormValues = z.infer<typeof skuSchema>;

export type SkuFormItem = {
  id?: string;
  productId?: string | null;
  code?: string | null;
  name?: string | null;
  saleType?: string | null;
  billingPeriodUnit?: string | null;
  billingPeriodCount?: number | null;
  defaultSeatQuantity?: number | null;
  minQuantity?: number | null;
  maxQuantity?: number | null;
  tier?: number | null;
  status?: string | null;
  metadata?: Record<string, unknown> | null;
};

function readFeatureConfig(metadata?: Record<string, unknown> | null): FeatureConfigItem[] | null {
  const config = metadata?.featureConfig;
  return Array.isArray(config) ? (config as FeatureConfigItem[]) : null;
}

function mergeSkuMetadata(
  existing: Record<string, unknown> | null | undefined,
  featureConfig: FeatureConfigItem[] | null,
  isPlan: boolean
): Record<string, unknown> | null {
  const next = existing && typeof existing === 'object' ? { ...existing } : {};
  if (isPlan) {
    if (featureConfig) {
      next.featureConfig = featureConfig;
    } else {
      delete next.featureConfig;
    }
  }
  return Object.keys(next).length > 0 ? next : null;
}

interface SkuEditorModalProps {
  open: boolean;
  productId: string;
  productType?: string | null;
  sku?: SkuFormItem | null;
  onCancel: () => void;
  onSuccess: () => void;
}

function toOptionalInt(value: number | null | undefined): number | null {
  if (value == null || Number.isNaN(value)) return null;
  return value;
}

function buildDefaultValues(sku?: SkuFormItem | null, isPlan = false): SkuFormValues {
  const featureGroups = isPlan
    ? parseFeatureConfigToGroups(readFeatureConfig(sku?.metadata))
    : undefined;

  if (sku) {
    return {
      code: sku.code || '',
      name: sku.name || '',
      saleType: (SALE_TYPES as readonly string[]).includes(sku.saleType ?? '')
        ? (sku.saleType as (typeof SALE_TYPES)[number])
        : 'SUBSCRIPTION',
      billingPeriodUnit: (BILLING_PERIOD_UNITS as readonly string[]).includes(
        sku.billingPeriodUnit ?? ''
      )
        ? (sku.billingPeriodUnit as (typeof BILLING_PERIOD_UNITS)[number])
        : 'MONTH',
      billingPeriodCount: sku.billingPeriodCount ?? 1,
      defaultSeatQuantity: sku.defaultSeatQuantity ?? null,
      minQuantity: sku.minQuantity ?? null,
      maxQuantity: sku.maxQuantity ?? null,
      tier: sku.tier ?? null,
      status: (SKU_STATUSES as readonly string[]).includes(sku.status ?? '')
        ? (sku.status as (typeof SKU_STATUSES)[number])
        : 'ACTIVE',
      featureGroups,
    };
  }
  return {
    code: '',
    name: '',
    saleType: 'SUBSCRIPTION',
    billingPeriodUnit: 'MONTH',
    billingPeriodCount: 1,
    defaultSeatQuantity: 1,
    minQuantity: 1,
    maxQuantity: null,
    tier: null,
    status: 'ACTIVE',
    featureGroups: isPlan ? [createEmptyGroup(1, 'A')] : undefined,
  };
}

export function SkuEditorModal({
  open,
  productId,
  productType,
  sku,
  onCancel,
  onSuccess,
}: SkuEditorModalProps) {
  const utils = trpc.useUtils();
  const isEdit = Boolean(sku?.id);
  const isPlan = productType === 'PLAN';
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const handleModalOpenChange = useCallback((next: boolean) => {
    if (!next) onCancelRef.current();
  }, []);

  const form = useForm<SkuFormValues>({
    resolver: zodResolver(skuSchema) as any,
    defaultValues: buildDefaultValues(sku, isPlan),
    mode: 'onChange',
  });
  const { reset, control, getValues } = form;
  const saleType = useWatch({ control, name: 'saleType' });
  const errors = form.formState.errors;

  useEffect(() => {
    if (!open) {
      reset(buildDefaultValues(undefined, isPlan));
      return;
    }
    reset(buildDefaultValues(sku, isPlan));
  }, [open, sku, isPlan, reset]);

  useEffect(() => {
    if (!open) return;
    if (saleType === 'ONE_TIME') {
      form.setValue('billingPeriodUnit', 'NONE');
      form.setValue('billingPeriodCount', 0);
    } else if (form.getValues('billingPeriodUnit') === 'NONE') {
      form.setValue('billingPeriodUnit', 'MONTH');
      if (form.getValues('billingPeriodCount') === 0) {
        form.setValue('billingPeriodCount', 1);
      }
    }
  }, [saleType, open, form]);

  const createMutation = trpc.bizProduct.sku.create.useMutation({
    onSuccess: () => {
      toast.success('SKU 创建成功');
      form.reset();
      onSuccess();
      void utils.bizProduct.sku.listAll.invalidate();
    },
    onError: (error) => {
      toast.danger('创建失败', { description: error.message });
    },
  });

  const updateMutation = trpc.bizProduct.sku.update.useMutation({
    onSuccess: () => {
      toast.success('SKU 更新成功');
      form.reset();
      onSuccess();
      void utils.bizProduct.sku.listAll.invalidate();
    },
    onError: (error) => {
      toast.danger('更新失败', { description: error.message });
    },
  });

  const onSubmit = async (values: SkuFormValues) => {
    const featureConfig = isPlan ? flattenFeatureGroups(values.featureGroups) : null;
    const payload = {
      code: values.code,
      name: values.name,
      saleType: values.saleType,
      billingPeriodUnit: values.billingPeriodUnit,
      billingPeriodCount: values.billingPeriodCount,
      defaultSeatQuantity: toOptionalInt(values.defaultSeatQuantity),
      minQuantity: toOptionalInt(values.minQuantity),
      maxQuantity: toOptionalInt(values.maxQuantity),
      tier: isPlan ? (toOptionalInt(values.tier) ?? 0) : null,
      status: values.status,
      metadata: mergeSkuMetadata(sku?.metadata, featureConfig, isPlan),
    };

    if (isEdit && sku?.id) {
      await updateMutation.mutateAsync({ id: sku.id, data: payload });
    } else {
      await createMutation.mutateAsync({ productId, ...payload });
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container
          placement="center"
          scroll="inside"
          size={isPlan ? 'cover' : 'lg'}
          className={isPlan ? 'max-w-4xl' : 'max-w-2xl'}
        >
          <Modal.Dialog
            key={open ? (isEdit && sku?.id ? `edit-${sku.id}` : 'create') : 'closed'}
          >
            <Modal.CloseTrigger />
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">
                {isEdit ? '编辑 SKU' : '新建 SKU'}
              </Modal.Heading>
              <Description className="text-sm">
                {isEdit ? '修改 SKU 信息' : '填写 SKU 信息以创建新规格'}
              </Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-2">
              <form
                id="editor-sku-form"
                onSubmit={form.handleSubmit(onSubmit as any)}
                className="flex min-w-0 flex-col gap-4"
              >
                <Controller
                  control={control}
                  name="code"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.code}>
                      <Label className="text-sm font-medium">
                        SKU 编码 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={64}
                        placeholder="如 PERSONAL_PLAN_STD_M"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.code ? <FieldError>{errors.code.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.name}>
                      <Label className="text-sm font-medium">
                        SKU 名称 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={128}
                        placeholder="请输入 SKU 名称"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.name ? <FieldError>{errors.name.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <div className="grid gap-4 sm:grid-cols-2">
                  <Controller
                    control={control}
                    name="saleType"
                    render={({ field }) => (
                      <Select
                        className="w-full"
                        isInvalid={!!errors.saleType}
                        variant="secondary"
                        value={field.value}
                        onChange={(key) => {
                          if (key != null) field.onChange(String(key));
                        }}
                      >
                        <Label className="text-sm font-medium">
                          售卖方式 <span className="text-danger">*</span>
                        </Label>
                        <Select.Trigger>
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            {SALE_TYPES.map((t) => (
                              <ListBox.Item key={t} id={t} textValue={SALE_TYPE_LABELS[t]}>
                                {SALE_TYPE_LABELS[t]}
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                        {errors.saleType ? (
                          <FieldError>{errors.saleType.message}</FieldError>
                        ) : null}
                      </Select>
                    )}
                  />

                  <Controller
                    control={control}
                    name="billingPeriodUnit"
                    render={({ field }) => (
                      <Select
                        className="w-full"
                        isInvalid={!!errors.billingPeriodUnit}
                        isDisabled={saleType === 'ONE_TIME'}
                        variant="secondary"
                        value={field.value}
                        onChange={(key) => {
                          if (key != null) field.onChange(String(key));
                        }}
                      >
                        <Label className="text-sm font-medium">
                          计费周期 <span className="text-danger">*</span>
                        </Label>
                        <Select.Trigger>
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            {BILLING_PERIOD_UNITS.map((u) => (
                              <ListBox.Item key={u} id={u} textValue={BILLING_PERIOD_LABELS[u]}>
                                {BILLING_PERIOD_LABELS[u]}
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                        {errors.billingPeriodUnit ? (
                          <FieldError>{errors.billingPeriodUnit.message}</FieldError>
                        ) : null}
                      </Select>
                    )}
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Controller
                    control={control}
                    name="billingPeriodCount"
                    render={({ field }) => (
                      <NumberField
                        className="w-full"
                        variant="secondary"
                        isInvalid={!!errors.billingPeriodCount}
                        isDisabled={saleType === 'ONE_TIME'}
                        minValue={0}
                        value={field.value}
                        onChange={(v) => field.onChange(v ?? 0)}
                      >
                        <Label className="text-sm font-medium">基础周期数量</Label>
                        <NumberField.Group>
                          <NumberField.DecrementButton />
                          <NumberField.Input className="w-full" />
                          <NumberField.IncrementButton />
                        </NumberField.Group>
                        {errors.billingPeriodCount ? (
                          <FieldError>{errors.billingPeriodCount.message}</FieldError>
                        ) : null}
                      </NumberField>
                    )}
                  />

                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <Select
                        className="w-full"
                        isInvalid={!!errors.status}
                        variant="secondary"
                        value={field.value}
                        onChange={(key) => {
                          if (key != null) field.onChange(String(key));
                        }}
                      >
                        <Label className="text-sm font-medium">
                          状态 <span className="text-danger">*</span>
                        </Label>
                        <Select.Trigger>
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            {SKU_STATUSES.map((s) => (
                              <ListBox.Item key={s} id={s} textValue={STATUS_LABELS[s]}>
                                {STATUS_LABELS[s]}
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                        {errors.status ? <FieldError>{errors.status.message}</FieldError> : null}
                      </Select>
                    )}
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                  <Controller
                    control={control}
                    name="defaultSeatQuantity"
                    render={({ field }) => (
                      <NumberField
                        className="w-full"
                        variant="secondary"
                        minValue={1}
                        value={field.value ?? undefined}
                        onChange={(v) => field.onChange(v == null ? null : v)}
                      >
                        <Label className="text-sm font-medium">默认席位数</Label>
                        <NumberField.Group>
                          <NumberField.DecrementButton />
                          <NumberField.Input className="w-full" />
                          <NumberField.IncrementButton />
                        </NumberField.Group>
                      </NumberField>
                    )}
                  />
                  <Controller
                    control={control}
                    name="minQuantity"
                    render={({ field }) => (
                      <NumberField
                        className="w-full"
                        variant="secondary"
                        minValue={1}
                        value={field.value ?? undefined}
                        onChange={(v) => field.onChange(v == null ? null : v)}
                      >
                        <Label className="text-sm font-medium">最小购买量</Label>
                        <NumberField.Group>
                          <NumberField.DecrementButton />
                          <NumberField.Input className="w-full" />
                          <NumberField.IncrementButton />
                        </NumberField.Group>
                      </NumberField>
                    )}
                  />
                  <Controller
                    control={control}
                    name="maxQuantity"
                    render={({ field }) => (
                      <NumberField
                        className="w-full"
                        variant="secondary"
                        minValue={1}
                        value={field.value ?? undefined}
                        onChange={(v) => field.onChange(v == null ? null : v)}
                      >
                        <Label className="text-sm font-medium">最大购买量</Label>
                        <NumberField.Group>
                          <NumberField.DecrementButton />
                          <NumberField.Input className="w-full" />
                          <NumberField.IncrementButton />
                        </NumberField.Group>
                        <Description className="text-xs">空表示不限制</Description>
                      </NumberField>
                    )}
                  />
                </div>

                {isPlan ? (
                  <Controller
                    control={control}
                    name="tier"
                    render={({ field }) => (
                      <Select
                        className="w-full"
                        isInvalid={!!errors.tier}
                        variant="secondary"
                        value={field.value != null ? String(field.value) : undefined}
                        onChange={(key) => {
                          if (key != null) field.onChange(Number(key));
                        }}
                      >
                        <Label className="text-sm font-medium">
                          档位 <span className="text-danger">*</span>
                        </Label>
                        <Select.Trigger>
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            {PLAN_TIERS.map((t) => (
                              <ListBox.Item key={t} id={String(t)} textValue={TIER_LABELS[t]}>
                                {TIER_LABELS[t]}
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                        {errors.tier ? <FieldError>{errors.tier.message}</FieldError> : null}
                      </Select>
                    )}
                  />
                ) : null}

                {isPlan ? (
                  <FeatureConfigEditor
                    control={control as any}
                    getValues={getValues as any}
                    errors={errors as any}
                  />
                ) : null}
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={onCancel}>
                取消
              </Button>
              <Button
                isDisabled={isLoading}
                variant="primary"
                onPress={() => void form.handleSubmit(onSubmit as any)()}
              >
                {isLoading ? '提交中…' : isEdit ? '更新' : '创建'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
